import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  getNormalizedTableList,
  type NormalizedTableListItem,
} from '../../services/data-integration-api';

/**
 * 数据审计入口 — 列出所有已创建的规范存取表，点击进入数据审计
 */
export default function AuditGateway() {
  const navigate = useNavigate();
  const [tables, setTables] = useState<NormalizedTableListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const result = await getNormalizedTableList({ creationStatus: 'created', page, pageSize });
      setTables(result.items);
      setTotal(result.total);
    } catch (e) {
      console.error('获取规范存取表列表失败:', e);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const syncStatusLabel = (status: string | null) => {
    const map: Record<string, string> = {
      completed: '同步成功',
      running: '同步中',
      failed: '同步失败',
      partial: '部分成功',
      pending: '等待中',
    };
    return status ? map[status] || status : '未同步';
  };

  const syncStatusVariant = (status: string | null): 'default' | 'destructive' | 'secondary' => {
    if (status === 'completed') return 'default';
    if (status === 'failed') return 'destructive';
    return 'secondary';
  };

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <h2 className="text-base font-semibold">数据审计</h2>
      </div>

      <div className="p-6">
        {loading ? (
          <p className="text-muted-foreground">加载中...</p>
        ) : fetchError ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">加载失败，请重试</p>
            <Button onClick={fetchList}>重试</Button>
          </div>
        ) : tables.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">暂无已创建的规范存取表</p>
            <Button onClick={() => navigate('/data-integration/connections')}>
              前往连接管理
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>规范表名</TableHead>
                  <TableHead>源表</TableHead>
                  <TableHead>源库连接</TableHead>
                  <TableHead>同步状态</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tables.map(t => (
                  <TableRow key={t._id}>
                    <TableCell className="font-medium">{t.normalized_table_name}</TableCell>
                    <TableCell className="font-mono">{t.source_table_name}</TableCell>
                    <TableCell>{t.source_connection_name}</TableCell>
                    <TableCell>
                      <Badge variant={syncStatusVariant(t.last_sync_status)}>
                        {syncStatusLabel(t.last_sync_status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/data-integration/audit/${t._id}`)}
                        >
                          查看数据
                        </Button>
                        {(t.last_sync_status === 'completed' || t.last_sync_status === 'partial') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/data-integration/prod-write/${t._id}`)}
                          >
                            写入生产库
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {total > pageSize && (
              <div className="mt-4 flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  上一页
                </Button>
                <span className="text-sm text-muted-foreground">
                  第 {page} 页 / 共 {Math.ceil(total / pageSize)} 页
                </span>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>
                  下一页
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
